import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AssessmentIcon from "@mui/icons-material/Assessment";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BarChartIcon from "@mui/icons-material/BarChart";
import { CircularProgress as MuiCircularProgress } from "@mui/material";
import PageWrapper from "../../components/pageWrapper/PageWrapper";
import Alert from "../../components/customMui/Alert";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import { setCertCodeInFocus, setCertCodeSubscribedList, setCertList } from "../../redux/slices/practiceSlice";
import pagePath from "../../util/constants/pagePath";
import getScoreColor from "../../util/functions/getScoreColor";
import "./dashboard.css";

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { certList, certCodeInFocus } = useSelector((state) => state.practice);
    const [pageLoading, setPageLoading] = useState(true);
    const [examHistory, setExamHistory] = useState([]);
    const [fullExamSummaries, setFullExamSummaries] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [dashboardCertCode, setDashboardCertCode] = useState(null);
    const refreshRequestIdRef = useRef(0);

    useEffect(() => {
        refreshDashboard();
    }, []);

    useEffect(() => {
        if (!certCodeInFocus || !dashboardCertCode || pageLoading || certCodeInFocus === dashboardCertCode) {
            return;
        }
        refreshDashboard();
    }, [certCodeInFocus, dashboardCertCode, pageLoading]);

    const refreshDashboard = async () => {
        const requestId = refreshRequestIdRef.current + 1;
        refreshRequestIdRef.current = requestId;
        setErrorMessage(null);
        setPageLoading(true);
        setExamHistory([]);
        setFullExamSummaries([]);

        const certListRes = await standardBizApiClient.certification_list();
        if (requestId !== refreshRequestIdRef.current) {
            return;
        }
        if (certListRes?.is_error) {
            setErrorMessage("Error while loading dashboard details, please try again soon.");
            setPageLoading(false);
            return;
        }

        const { cert_code_in_focus, cert_code_subscribed_list, cert_list } = certListRes.certs;
        dispatch(setCertCodeInFocus(cert_code_in_focus));
        dispatch(setCertCodeSubscribedList(cert_code_subscribed_list));
        dispatch(setCertList(cert_list));
        setDashboardCertCode(cert_code_in_focus);

        if (cert_code_subscribed_list.length === 0) {
            navigate(pagePath.practice_no_subscribed_certs);
            return;
        }

        const fullExamHistoryRes = await standardBizApiClient.exam_full_history();
        if (requestId !== refreshRequestIdRef.current) {
            return;
        }
        if (!fullExamHistoryRes?.is_error) {
            const fullPracticeExams = fullExamHistoryRes.full_practice_exams || [];
            setExamHistory(fullPracticeExams);
            setPageLoading(false);

            const fullSummaryResults = await Promise.all(
                fullPracticeExams.map((exam) => standardBizApiClient.exam_summary(exam.practice_exam_id))
            );
            if (requestId !== refreshRequestIdRef.current) {
                return;
            }
            setFullExamSummaries(fullSummaryResults.filter((summary) => !summary?.is_error));
            return;
        }

        setPageLoading(false);
    };

    const certInFocus = useMemo(() => {
        return certList.find((cert) => cert.cert.code === certCodeInFocus) || null;
    }, [certList, certCodeInFocus]);

    const dashboardStats = useMemo(() => {
        const domains = certInFocus?.domains || [];
        const domainTests = domains.flatMap((domain) => domain.test_history || []);
        const fullTests = examHistory || [];
        const allTests = [...fullTests, ...domainTests];

        const answered = allTests.reduce((acc, test) => {
            const correct = Number(test.peq_answer_pass || 0);
            const incorrect = Number(test.peq_answer_fail || 0);
            return acc + Number(test.peq_answered || correct + incorrect || 0);
        }, 0);

        const correct = allTests.reduce((acc, test) => acc + Number(test.peq_answer_pass || 0), 0);
        const totalKnownQuestions = allTests.reduce((acc, test) => acc + Number(test.peq_count || 0), 0);
        const testsCompleted = allTests.length;
        const scoreSamples = allTests.map((test) => Number(test.score_factor || 0)).filter((score) => !Number.isNaN(score));
        const averageScore = scoreSamples.length > 0
            ? scoreSamples.reduce((acc, score) => acc + score, 0) / scoreSamples.length
            : 0;
        const accuracy = answered > 0 ? correct / answered : averageScore;
        const timeSpent = allTests.reduce((acc, test) => acc + Number(test.time_spent_seconds || 0), 0);
        const avgSeconds = answered > 0 ? Math.round(timeSpent / answered) : 0;
        const bestScore = scoreSamples.length > 0 ? Math.max(...scoreSamples) : 0;
        const domainMetrics = domains.map((domain) => {
            const domainId = String(domain.domain.cert_domain_id);
            let domainCorrect = 0;
            let domainTotal = 0;

            (domain.test_history || []).forEach((test) => {
                domainCorrect += Number(test.peq_answer_pass || 0);
                domainTotal += Number(test.peq_count || 0);
            });

            fullExamSummaries.forEach((summary) => {
                (summary.practice_question_list || []).forEach((practiceQuestion) => {
                    if (String(practiceQuestion.question.cert_domain_id || "") !== domainId) return;
                    domainTotal += 1;
                    if (practiceQuestion.practice.question_state === "answer_pass") {
                        domainCorrect += 1;
                    }
                });
            });

            const scoreFactor = domainTotal > 0
                ? domainCorrect / domainTotal
                : Number(domain.assoc?.score_factor || 0);

            return {
                id: domain.domain.cert_domain_id,
                name: domain.domain.name,
                correct: domainCorrect,
                total: domainTotal,
                scorePercent: Math.round(scoreFactor * 100)
            };
        }).sort((a, b) => {
            const aNum = Number((a.name.match(/domain\s+(\d+)/i) || [])[1]);
            const bNum = Number((b.name.match(/domain\s+(\d+)/i) || [])[1]);
            if (aNum && bNum && aNum !== bNum) return aNum - bNum;
            return a.name.localeCompare(b.name, undefined, { numeric: true });
        });

        return {
            answered,
            correct,
            totalKnownQuestions,
            testsCompleted,
            accuracyPercent: Math.round(accuracy * 100),
            avgSeconds,
            bestScorePercent: Math.round(bestScore * 100),
            domainMetrics
        };
    }, [certInFocus, examHistory, fullExamSummaries]);

    if (pageLoading) {
        return (
            <PageWrapper className="dashboard-page dashboard-page-loading">
                <MuiCircularProgress />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className="dashboard-page">
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            <section className="dashboard-hero">
                <div className="dashboard-hero-title">
                    <span><AssessmentIcon /></span>
                    <div>
                        <h1>Dashboard</h1>
                        <p>Track your practice activity and performance</p>
                    </div>
                </div>
            </section>

            <section className="dashboard-stat-grid">
                <div
                    className="dashboard-stat-card dashboard-stat-card-score"
                    style={{ "--dashboard-score-color": getScoreColor(dashboardStats.accuracyPercent) }}
                >
                    <QueryStatsIcon />
                    <strong>{dashboardStats.accuracyPercent}%</strong>
                    <p>Overall Accuracy</p>
                    <small>{dashboardStats.correct} correct / {dashboardStats.answered || dashboardStats.totalKnownQuestions || 0} attempted</small>
                </div>
                <div className="dashboard-stat-card dashboard-stat-card-primary">
                    <FactCheckIcon />
                    <strong>{dashboardStats.answered}</strong>
                    <p>Questions Answered</p>
                    <small>{dashboardStats.totalKnownQuestions ? `${dashboardStats.totalKnownQuestions} questions attempted` : "No questions attempted yet"}</small>
                </div>
                <div className="dashboard-stat-card dashboard-stat-card-success">
                    <AccessTimeFilledIcon />
                    <strong>{dashboardStats.avgSeconds}s</strong>
                    <p>Avg. Time/Question</p>
                    <small>{dashboardStats.avgSeconds <= 3 ? "Good pace" : "Steady pace"}</small>
                </div>
                <div className="dashboard-stat-card dashboard-stat-card-neutral">
                    <AssignmentTurnedInIcon />
                    <strong>{dashboardStats.testsCompleted}</strong>
                    <p>Tests Completed</p>
                    <small>Best score: {dashboardStats.bestScorePercent}%</small>
                </div>
            </section>

            <section className="dashboard-domain-section">
                <h2><BarChartIcon /> Domain Accuracy</h2>
                <div className="dashboard-domain-grid">
                    {dashboardStats.domainMetrics.map((domain) => {
                        const scoreColor = getScoreColor(domain.scorePercent);
                        return (
                            <div
                                key={domain.id}
                                className="dashboard-domain-card"
                                style={{ "--dashboard-score-color": scoreColor }}
                            >
                                <div className="dashboard-domain-card-header">
                                    <strong>{domain.name}</strong>
                                    <span>{domain.scorePercent}%</span>
                                </div>
                                <p>
                                    {domain.total > 0 ? (
                                        <>{domain.correct} correct <span>•</span> {domain.total} attempted questions</>
                                    ) : (
                                        <>No attempted questions yet</>
                                    )}
                                </p>
                                <div className="dashboard-domain-progress"><div style={{ width: `${domain.scorePercent}%` }} /></div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </PageWrapper>
    )
}

export default Dashboard;
